import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Wrench, Settings, CheckCircle, Shield, Zap, Gauge, Sliders, RotateCcw, AlertOctagon, ClipboardCheck } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import banner from '../../assets/Images/infra/img7.jpg';
import banner1 from '../../assets/Images/services/controlValves.webp';

// Animation variants
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.2,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

const ControlValveRefurbishment = () => {
  const navigate = useNavigate();
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });
  
  useEffect(() => { if (inView) controls.start('show'); }, [controls, inView]);

  const keyPoints = [
    'Complete disassembly, cleaning, and inspection of control valves',
    'Precision machining and reconditioning of valve components',
    'Replacement of all seals, gaskets, and wearing parts',
    'Calibration and testing to ensure optimal performance',
    'Documented test results and performance certification',
  ];

  const processSteps = [
    { icon: <AlertOctagon className="text-blue-600" size={24} />, title: 'Initial Inspection', description: 'Thorough visual inspection and performance testing.' },
    { icon: <Wrench className="text-blue-600" size={24} />, title: 'Disassembly', description: 'Complete disassembly of valve components.' },
    { icon: <Sliders className="text-blue-600" size={24} />, title: 'Component Refurbishment', description: 'Reconditioning of valve trim and body.' },
    { icon: <RotateCcw className="text-blue-600" size={24} />, title: 'Seal Replacement', description: 'Installation of new seals and gaskets.' },
    { icon: <Gauge className="text-blue-600" size={24} />, title: 'Calibration', description: 'Precise calibration to specifications.' },
    { icon: <ClipboardCheck className="text-blue-600" size={24} />, title: 'Testing', description: 'Rigorous performance testing.' },
  ];

  const objectives = [
    'Restore control valves to like-new condition',
    'Ensure precise flow control and tight shut-off',
    'Extend service life of critical components',
    'Reduce maintenance costs and unplanned downtime',
    'Comply with industry standards',
  ];

  return (
    <div className="text-gray-800 leading-relaxed max-w-full overflow-x-hidden pt-20 md:pt-24">
      <Header />

      {/* Hero Section */}
      <motion.section initial="hidden" animate="show" variants={container} className="relative bg-gray-50 py-16 md:py-24 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">
            <motion.div variants={item} className="md:w-1/2 mb-10 md:mb-0 md:pr-10">
              <motion.h1 variants={fadeInUp} className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#00325b] mb-6">
                Control Valve Refurbishment
              </motion.h1>
              <motion.p variants={fadeInUp} className="text-lg sm:text-xl text-gray-700 mb-8">
                Expert refurbishment services that restore control valves to optimal performance and reliability.
              </motion.p>
              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
                <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }} onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })} className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-8 rounded-lg transition-all">
                  Get a Free Quote
                </motion.button>
                <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }} onClick={() => navigate('/contact')} className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-medium py-3 px-6 rounded-lg transition-all flex items-center gap-2">
                  <Shield size={20} /> Service Enquiry
                </motion.button>
              </motion.div>
            </motion.div>
            <motion.div variants={item} className="md:w-1/2 relative">
              <div className="flex flex-col md:flex-row gap-6 w-full">
                <div className="w-full md:w-1/2 h-[300px] md:h-[400px] overflow-hidden">
                  <motion.img
                    src={banner}
                    alt="Control Valve Refurbishment"
                    className="w-full h-full object-cover rounded-xl shadow-2xl hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="w-full md:w-1/2 h-[300px] md:h-[400px] overflow-hidden">
                  <motion.img
                    src={banner1}
                    alt="Control Valve Refurbishment"
                    className="w-full h-full object-cover rounded-xl shadow-2xl hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Description Section */}
      <motion.section ref={ref} initial="hidden" animate={controls} variants={container} className="py-20 bg-gradient-to-br from-blue-50 to-gray-50">
        <div className="container mx-auto px-4">
          <motion.div variants={container} className="max-w-5xl mx-auto text-center mb-16">
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Precision <span className="text-blue-600">Valve Restoration</span>
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-gray-700 max-w-3xl mx-auto">
              Our expert technicians restore control valves to like-new condition using precision tools and quality components.
            </motion.p>
          </motion.div>

          <motion.div variants={container} className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <motion.div variants={item} className="bg-white p-8 rounded-2xl shadow-lg border hover:shadow-xl transition-all">
              <div className="bg-blue-50 w-14 h-14 flex items-center justify-center rounded-xl mb-6">
                <Zap className="text-blue-600" size={28} />
              </div>
              <h3 className="text-2xl font-semibold mb-6 text-[#00325b]">Why Choose Us</h3>
              <ul className="space-y-4">
                {keyPoints.map((point, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle className="text-green-600" size={18} />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div variants={item} className="bg-white p-8 rounded-2xl shadow-lg border hover:shadow-xl transition-all">
              <div className="bg-blue-50 w-14 h-14 flex items-center justify-center rounded-xl mb-6">
                <Settings className="text-blue-600" size={28} />
              </div>
              <h3 className="text-2xl font-semibold mb-6 text-[#00325b]">Our Objectives</h3>
              <ul className="space-y-4">
                {objectives.map((obj, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle className="text-green-600" size={18} />
                    <span>{obj}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Process Section */}
      {/* <motion.section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-12">
            <motion.h2 className="text-3xl font-bold text-[#00325b] mb-4">
              Our Refurbishment Process
            </motion.h2>
            <motion.p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our comprehensive 6-step process ensures your control valves are restored to optimal performance.
            </motion.p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 p-6 rounded-xl border border-gray-100 hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-blue-50 mb-4">
                  {step.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2 text-gray-900">
                  {step.title}
                </h3>
                <p className="text-gray-600">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section> */}

      <Footer />
    </div>
  );
};

export default ControlValveRefurbishment;
